function renderHookContentTemplate(): string {
  return `      if (safePageSize !== -1) {
        filters.limit = safePageSize
        filters.offset = safePage * safePageSize
      }
      if (orderBy) filters.orderBy = orderBy
      if (orderDirection) filters.orderDirection = orderDirection`
}

function renderHookFiltersTemplate({
  plural,
  map,
  paginationBlock
}: {
  plural: string
  map: string
  paginationBlock: string
}): string {
  return `const filters: ${plural}Query = {}
      if (search) filters.search = search
${map}
${paginationBlock}
      return get${plural}(Object.keys(filters).length > 0 ? filters : undefined)`
}

function renderHookContentDetailsTemplate(): string {
  return `      if (status === 'published') filters.published = true
      if (status === 'draft') filters.published = false`
}

function renderHookContentDetailsBranchTemplate({
  filterFromVariableName,
  filterField,
  filterToVariableName
}: {
  filterFromVariableName: string
  filterField: string
  filterToVariableName: string
}): string {
  return `      if (${filterFromVariableName}) filters.${filterField}From = ${filterFromVariableName}
      if (${filterToVariableName}) filters.${filterField}To = ${filterToVariableName}`
}

function renderHookContentDetailsBranchBodyTemplate({
  filterVariableName,
  filterField
}: {
  filterVariableName: string
  filterField: string
}): string {
  return `      if (${filterVariableName} === 'true') filters.${filterField} = true
      if (${filterVariableName} === 'false') filters.${filterField} = false`
}

function renderHookGeneratedFunctionTemplate({
  singular,
  singularText
}: {
  singular: string
  singularText: string
}): string {
  return `
export function use${singular}(id: string | null | undefined): UseQueryResult<${singular} | null, Error> {
  return useQuery({
    queryKey: ['${singularText}', id],
    queryFn: () => (id ? get${singular}ById(id) : Promise.resolve(null)),
    enabled: !!id,
  })
}`
}

function renderHookGeneratedFunctionDetailsTemplate({
  singular,
  singularText
}: {
  singular: string
  singularText: string
}): string {
  return `

export function use${singular}BySlug(slug: string | null | undefined): UseQueryResult<${singular} | null, Error> {
  return useQuery({
    queryKey: ['${singularText}', 'slug', slug],
    queryFn: () => (slug ? get${singular}BySlug(slug) : Promise.resolve(null)),
    enabled: !!slug,
  })
}`
}

function renderHookGeneratedFunctionDetailsBranchTemplate({
  plural,
  filterPascalName,
  pluralText,
  filterKey
}: {
  plural: string
  filterPascalName: string
  pluralText: string
  filterKey: string
}): string {
  return `

export function use${plural}Distinct${filterPascalName}(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['${pluralText}', 'distinct', '${filterKey}'],
    queryFn: () => getDistinct${plural}${filterPascalName}()
  })
}`
}

function renderHookGeneratedFunctionDetailsBranchBodyTemplate({
  join,
  actionImportPath,
  plural,
  allParamsContent,
  pluralText,
  queryKeyParts,
  filtersBody,
  singularHook,
  slugHook,
  distinctHooks
}: {
  join: string
  actionImportPath: string
  plural: string
  allParamsContent: string
  pluralText: string
  queryKeyParts: string
  filtersBody: string
  singularHook: string
  slugHook: string
  distinctHooks: string
}): string {
  return `import {
  ${join}
} from '@admin/actions/${actionImportPath}'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'

export function use${plural}(
  ${allParamsContent}
  options?: { enabled?: boolean }
): UseQueryResult<${plural}Page, Error> {
  const safePage = Number.isFinite(page ?? 0) && (page ?? 0) > 0 ? (page ?? 0) : 0
  const safePageSize = pageSize === -1 || (Number.isFinite(pageSize ?? 20) && (pageSize ?? 20) > 0) ? (pageSize ?? 20) : 20

  return useQuery({
    queryKey: ['${pluralText}', ${queryKeyParts}],
    queryFn: () => {
      ${filtersBody}
    },
    enabled: options?.enabled ?? true,
  })
}
${singularHook}${slugHook}${distinctHooks}
`
}

export function renderHookRenderer(
  request:
    | { kind: 'renderHookContentTemplate' }
    | {
        kind: 'renderHookFiltersTemplate'
        props: {
          plural: string
          map: string
          paginationBlock: string
        }
      }
    | { kind: 'renderHookContentDetailsTemplate' }
    | {
        kind: 'renderHookContentDetailsBranchTemplate'
        props: {
          filterFromVariableName: string
          filterField: string
          filterToVariableName: string
        }
      }
    | {
        kind: 'renderHookContentDetailsBranchBodyTemplate'
        props: {
          filterVariableName: string
          filterField: string
        }
      }
    | {
        kind: 'renderHookGeneratedFunctionTemplate'
        props: {
          singular: string
          singularText: string
        }
      }
    | {
        kind: 'renderHookGeneratedFunctionDetailsTemplate'
        props: {
          singular: string
          singularText: string
        }
      }
    | {
        kind: 'renderHookGeneratedFunctionDetailsBranchTemplate'
        props: {
          plural: string
          filterPascalName: string
          pluralText: string
          filterKey: string
        }
      }
    | {
        kind: 'renderHookGeneratedFunctionDetailsBranchBodyTemplate'
        props: {
          join: string
          actionImportPath: string
          plural: string
          allParamsContent: string
          pluralText: string
          queryKeyParts: string
          filtersBody: string
          singularHook: string
          slugHook: string
          distinctHooks: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderHookContentTemplate':
      return renderHookContentTemplate()
    case 'renderHookFiltersTemplate':
      return renderHookFiltersTemplate(request.props)
    case 'renderHookContentDetailsTemplate':
      return renderHookContentDetailsTemplate()
    case 'renderHookContentDetailsBranchTemplate':
      return renderHookContentDetailsBranchTemplate(request.props)
    case 'renderHookContentDetailsBranchBodyTemplate':
      return renderHookContentDetailsBranchBodyTemplate(request.props)
    case 'renderHookGeneratedFunctionTemplate':
      return renderHookGeneratedFunctionTemplate(request.props)
    case 'renderHookGeneratedFunctionDetailsTemplate':
      return renderHookGeneratedFunctionDetailsTemplate(request.props)
    case 'renderHookGeneratedFunctionDetailsBranchTemplate':
      return renderHookGeneratedFunctionDetailsBranchTemplate(request.props)
    case 'renderHookGeneratedFunctionDetailsBranchBodyTemplate':
      return renderHookGeneratedFunctionDetailsBranchBodyTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
